// src/pages/dropshipping/DropshippingInvoiceListPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Building2, X, CheckCircle, Eye } from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import {
  getDropshippingInvoices,
  bulkUpdateDropshippingInvoiceStatuses,
  getProviderDropshipperShops,
} from '../../services/api/dropshipping';
import { formatDate } from '../../utils/dateUtils';
import styles from './DropshippingInvoiceListPage.module.css';

const STATUS_CLASSES = {
  generated: 'statusGenerated',
  paid: 'statusPaid',
  disputed: 'statusDisputed',
};

const DropshippingInvoiceListPage = () => {
  const { t, formatCurrency } = useLocalization();
  const { error: showError, success: showSuccess, confirm } = useAlert();
  const navigate = useNavigate();

  // ── Role toggle ─────────────────────────────────────────────────────
  const [role, setRole] = useState('provider');
  const isProvider = role === 'provider';

  // ── Data ────────────────────────────────────────────────────────────
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [dropshipperShops, setDropshipperShops] = useState([]);

  // ── Selection (provider only) ────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState([]);

  // ── Bank modal (provider only) ───────────────────────────────────────
  const [bankModalInvoice, setBankModalInvoice] = useState(null);

  // ── Pagination ───────────────────────────────────────────────────────
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [20, 50, 100],
  });

  // ── Filters ──────────────────────────────────────────────────────────
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState({
    status: '',
    shopId: '',
    dateFrom: '',
    dateTo: '',
  });

  // ── Load dropshipper shops (provider only) ───────────────────────────
  useEffect(() => {
    if (isProvider) {
      getProviderDropshipperShops()
        .then(setDropshipperShops)
        .catch(() => {});
    }
  }, [isProvider]);

  // ── Fetch invoices ───────────────────────────────────────────────────
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        role,
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
      };
      if (filterValues.status) params.status = filterValues.status;
      if (filterValues.shopId) params.shopId = filterValues.shopId;
      if (filterValues.dateFrom) params.dateFrom = filterValues.dateFrom;
      if (filterValues.dateTo) params.dateTo = filterValues.dateTo;

      const data = await getDropshippingInvoices(params);
      const rows = data.rows || data.invoices || [];
      const count = data.count || 0;
      setInvoices(rows);
      setTotalCount(count);
      setPagination((prev) => ({ ...prev, total: count }));
    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  }, [role, pagination.currentPage, pagination.pageSize, filterValues]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // ── Role switch resets everything ────────────────────────────────────
  const handleRoleChange = (newRole) => {
    setRole(newRole);
    setSelectedIds([]);
    setFilterValues({ status: '', shopId: '', dateFrom: '', dateTo: '' });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // ── Filter handlers ──────────────────────────────────────────────────
  const handleFilterChange = (newValues) => {
    setFilterValues(newValues);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setFilterValues({ status: '', shopId: '', dateFrom: '', dateTo: '' });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // ── Selection: only 'generated' invoices are selectable ─────────────
  const handleSelectionChange = (ids) => {
    const filtered = ids.filter((id) => {
      const inv = invoices.find((i) => i.id === id);
      return inv && inv.status === 'generated';
    });
    setSelectedIds(filtered);
  };

  // ── Bulk mark as paid ────────────────────────────────────────────────
  const handleMarkAsPaid = async () => {
    if (selectedIds.length === 0) return;
    const confirmed = await confirm(
      t('dropshipping.confirmMarkPaid', { count: selectedIds.length }),
      { title: t('dropshipping.markAsPaid') }
    );
    if (!confirmed) return;
    try {
      const result = await bulkUpdateDropshippingInvoiceStatuses(selectedIds, 'paid');
      showSuccess(t('dropshipping.invoicesMarkedPaid', { count: result.updated }));
      setSelectedIds([]);
      fetchInvoices();
    } catch (err) {
      showError(err.message);
    }
  };

  // ── CSV Export ───────────────────────────────────────────────────────
  const handleExport = () => {
    const baseHeaders = [
      t('dropshipping.invoiceNumber'),
      t('dropshipping.generatedAt'),
      t('dropshipping.shopName'),
      t('dropshipping.totalOrders'),
      t('dropshipping.totalDelivered'),
      t('dropshipping.totalReturned'),
      t('dropshipping.totalCodCollected'),
      t('dropshipping.totalCourierCharges'),
      t('dropshipping.totalDropshippingCharges'),
      t('dropshipping.totalServiceCharges'),
      t('dropshipping.totalReturnPenalties'),
      t('dropshipping.totalAmountPayable'),
      t('dropshipping.status'),
    ];

    const bankHeaders = isProvider
      ? [
          t('dropshipping.resellerAccountName'),
          t('dropshipping.resellerAccountNumber'),
          t('dropshipping.bankName'),
          t('dropshipping.bankShortCode'),
          t('dropshipping.branchName'),
          t('dropshipping.branchCode'),
        ]
      : [];

    const headers = [...baseHeaders, ...bankHeaders];

    const rows = invoices.map((inv) => {
      const base = [
        inv.invoiceNumber,
        formatDate(inv.generatedAt),
        inv.shop?.name || inv.shopId,
        inv.totalOrders,
        inv.totalDeliveredOrders,
        inv.totalReturnedOrders,
        inv.totalCodCollected,
        inv.totalCourierCharges,
        inv.totalDropshippingCharges,
        inv.totalServiceCharges,
        inv.totalReturnPenalties,
        inv.totalAmountPayable,
        inv.status,
      ];

      const bank = isProvider
        ? [
            inv.shop?.resellerAccountName || '',
            inv.shop?.resellerAccountNumber || '',
            inv.shop?.resellerBank?.name || '',
            inv.shop?.resellerBank?.shortCode || '',
            inv.shop?.resellerBankBranch?.branchName || '',
            inv.shop?.resellerBankBranch?.branchCode || '',
          ]
        : [];

      return [...base, ...bank];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dropshipping-invoices-${role}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ── Filter definitions ───────────────────────────────────────────────
  const filterDefinitions = [
    {
      key: 'status',
      label: t('dropshipping.status'),
      type: 'select',
      placeholder: t('dropshipping.allStatuses'),
      options: [
        { value: 'generated', label: t('dropshipping.generated') },
        { value: 'paid', label: t('dropshipping.paid') },
        { value: 'disputed', label: t('dropshipping.disputed') },
      ],
    },
    ...(isProvider
      ? [
          {
            key: 'shopId',
            label: t('dropshipping.shopName'),
            type: 'select',
            placeholder: t('dropshipping.allShops'),
            options: dropshipperShops.map((s) => ({ value: String(s.id), label: s.name })),
          },
        ]
      : []),
    {
      key: 'dateFrom',
      label: t('dropshipping.dateFrom'),
      type: 'date',
    },
    {
      key: 'dateTo',
      label: t('dropshipping.dateTo'),
      type: 'date',
    },
  ];

  // ── Column definitions ───────────────────────────────────────────────
  const columns = [
    {
      key: 'invoiceNumber',
      header: t('dropshipping.invoiceNumber'),
      sortable: false,
      render: (inv) => (
        <span className={styles.invoiceNumber}>{inv.invoiceNumber}</span>
      ),
    },
    {
      key: 'generatedAt',
      header: t('dropshipping.generatedAt'),
      render: (inv) => formatDate(inv.generatedAt),
    },
    {
      key: 'shopName',
      header: t('dropshipping.shopName'),
      render: (inv) => inv.shop?.name || inv.shopId,
    },
    {
      key: 'totalOrders',
      header: t('dropshipping.totalOrders'),
      align: 'right',
      render: (inv) => inv.totalOrders,
    },
    {
      key: 'totalAmountPayable',
      header: t('dropshipping.totalAmountPayable'),
      align: 'right',
      render: (inv) => (
        <span className={styles.amount}>{formatCurrency(inv.totalAmountPayable)}</span>
      ),
    },
    {
      key: 'status',
      header: t('dropshipping.status'),
      render: (inv) => (
        <span
          className={`${styles.statusBadge} ${styles[STATUS_CLASSES[inv.status] || 'statusGenerated']}`}
        >
          {t(`dropshipping.${inv.status}`)}
        </span>
      ),
    },
  ];

  // ── Actions ───────────────────────────────────────────────────────────
  const tableActions = {
    render: (inv) => (
      <div className={styles.actionBtns}>
        <button
          className={styles.actionBtn}
          title={t('dropshipping.viewInvoice')}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/dropshipping/invoices/${inv.id}`);
          }}
        >
          <Eye size={14} />
        </button>
        {isProvider && (
          <button
            className={styles.actionBtn}
            title={t('dropshipping.viewBankDetails')}
            onClick={(e) => {
              e.stopPropagation();
              setBankModalInvoice(inv);
            }}
          >
            <Building2 size={14} />
          </button>
        )}
      </div>
    ),
    width: isProvider ? '80px' : '50px',
    frozen: false,
  };

  // ── Row click ────────────────────────────────────────────────────────
  const handleRowClick = (inv) => {
    navigate(`/dropshipping/invoices/${inv.id}`);
  };

  // ── Selectable rows: only 'generated' invoices (provider only) ───────
  // DataTable shows checkboxes when selectable=true. We prevent selection
  // of non-generated rows by filtering in handleSelectionChange.
  const rowClassName = (inv) => {
    if (!isProvider) return '';
    return inv.status !== 'generated' ? styles.nonSelectableRow : '';
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('dropshipping.invoices')}
        icon={FileText}
      />

      {/* Role toggle */}
      <div className={styles.roleToggle}>
        <button
          className={`${styles.roleBtn} ${isProvider ? styles.roleBtnActive : ''}`}
          onClick={() => handleRoleChange('provider')}
        >
          {t('dropshipping.roleProvider')}
        </button>
        <button
          className={`${styles.roleBtn} ${!isProvider ? styles.roleBtnActive : ''}`}
          onClick={() => handleRoleChange('reseller')}
        >
          {t('dropshipping.roleReseller')}
        </button>
      </div>

      {/* Bulk actions bar (provider only) */}
      {isProvider && selectedIds.length > 0 && (
        <div className={styles.bulkActionsBar}>
          <span className={styles.selectedCount}>
            {t('dropshipping.invoicesSelected', { count: selectedIds.length })}
          </span>
          <div className={styles.bulkActions}>
            <button className={styles.markPaidBtn} onClick={handleMarkAsPaid}>
              <CheckCircle size={14} />
              {t('dropshipping.markAsPaid')}
            </button>
            <button className={styles.clearSelBtn} onClick={() => setSelectedIds([])}>
              <X size={14} />
              {t('dropshipping.clearSelection')}
            </button>
          </div>
        </div>
      )}

      {/* Filter Panel */}
      <FilterPanel
        filters={filterDefinitions}
        values={filterValues}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters((v) => !v)}
        showExport
        onExport={handleExport}
        exportButtonText={t('dropshipping.exportCsv')}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: (size) => setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 })),
          options: pagination.pageSizeOptions,
        }}
      />

      {/* Data Table */}
      <DataTable
        data={invoices}
        columns={columns}
        actions={tableActions}
        selectable={isProvider}
        selectedRows={isProvider ? selectedIds : []}
        onSelectionChange={isProvider ? handleSelectionChange : undefined}
        onRowClick={handleRowClick}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={loading}
        rowClassName={rowClassName}
        emptyMessage={t('dropshipping.noInvoicesFound')}
        emptyDescription={t('dropshipping.noInvoicesDescription')}
        emptyIcon={<FileText size={48} />}
      />

      {/* Bank Details Modal */}
      {bankModalInvoice && (
        <div className={styles.modalOverlay} onClick={() => setBankModalInvoice(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                <Building2 size={18} />
                {t('dropshipping.bankDetails')}
              </h3>
              <button className={styles.modalClose} onClick={() => setBankModalInvoice(null)}>
                <X size={18} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalShopName}>
                {bankModalInvoice.shop?.name || t('dropshipping.unknownShop')}
              </div>
              <div className={styles.bankDetailGrid}>
                <div className={styles.bankDetailItem}>
                  <span className={styles.bankLabel}>{t('dropshipping.resellerAccountName')}</span>
                  <span className={styles.bankValue}>
                    {bankModalInvoice.shop?.resellerAccountName || '-'}
                  </span>
                </div>
                <div className={styles.bankDetailItem}>
                  <span className={styles.bankLabel}>{t('dropshipping.resellerAccountNumber')}</span>
                  <span className={styles.bankValue}>
                    {bankModalInvoice.shop?.resellerAccountNumber || '-'}
                  </span>
                </div>
                <div className={styles.bankDetailItem}>
                  <span className={styles.bankLabel}>{t('dropshipping.bankName')}</span>
                  <span className={styles.bankValue}>
                    {bankModalInvoice.shop?.resellerBank?.name || '-'}
                    {bankModalInvoice.shop?.resellerBank?.shortCode
                      ? ` (${bankModalInvoice.shop.resellerBank.shortCode})`
                      : ''}
                  </span>
                </div>
                <div className={styles.bankDetailItem}>
                  <span className={styles.bankLabel}>{t('dropshipping.branchName')}</span>
                  <span className={styles.bankValue}>
                    {bankModalInvoice.shop?.resellerBankBranch?.branchName || '-'}
                    {bankModalInvoice.shop?.resellerBankBranch?.branchCode
                      ? ` (${bankModalInvoice.shop.resellerBankBranch.branchCode})`
                      : ''}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DropshippingInvoiceListPage;
